import { storage } from '@/lib/storage'

/** Resolve the public origin used for protected uploads. */
function uploadsBaseUrl(): string {
  const uploadsUrl = import.meta.env.VITE_UPLOADS_URL as string | undefined
  const apiUrl = import.meta.env.VITE_PUBLIC_API_URL as string | undefined
  for (const u of [uploadsUrl, apiUrl]) {
    if (u?.trim()) {
      try {
        const configured = new URL(u.trim()).origin
        const configuredHost = new URL(configured).hostname
        const currentHost = typeof location !== 'undefined' ? location.hostname : ''
        const configuredIsLocal =
          configuredHost === 'localhost' || configuredHost === '127.0.0.1' || configuredHost === '::1'
        const currentIsLocal = currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '::1'
        if (configuredIsLocal && currentHost && !currentIsLocal) {
          return location.origin
        }
        return configured
      } catch {
        /* ignore */
      }
    }
  }
  if (
    typeof location !== 'undefined' &&
    location.hostname === 'localhost' &&
    String(location.port || '') === '3000'
  ) {
    return 'http://localhost:8000'
  }
  if (typeof location !== 'undefined') return location.origin
  return ''
}

/**
 * Protected files are served through `/api/v1/uploads/...?token=`.
 */
export function resolveUploadsUrl(urlOrPath: string, opts: { version?: string } = {}): string {
  if (!urlOrPath?.trim()) return ''
  const s = urlOrPath.trim()
  const token = storage.accessToken()

  if (s.startsWith('data:')) return s

  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const url = new URL(s)
      let path = url.pathname
      if (path.includes('/servicedesk-data/')) {
        path = '/uploads/' + path.split('/servicedesk-data/')[1]
      }
      if (path.startsWith('/uploads/')) {
        path = '/api/v1' + path
      }
      const base = uploadsBaseUrl().replace(/\/+$/, '')
      let normalizedUrl = base + path
      if (token) {
        const finalUrl = new URL(normalizedUrl)
        finalUrl.searchParams.set('token', token)
        normalizedUrl = finalUrl.toString()
      }
      if (opts.version) {
        normalizedUrl += normalizedUrl.includes('?') ? '&' : '?'
        normalizedUrl += `v=${encodeURIComponent(String(opts.version))}`
      }
      return normalizedUrl
    } catch {
      return s
    }
  }

  const base = uploadsBaseUrl().replace(/\/+$/, '')
  let path = s.startsWith('/') ? s : `/${s}`
  if (path.startsWith('/uploads/')) {
    path = '/api/v1' + path
  } else if (!path.startsWith('/api/v1/uploads/')) {
    const cleanPath = path.replace(/^\/+/, '')
    const folder = cleanPath.startsWith('avatar_') ? 'avatars/' : ''
    path = `/api/v1/uploads/${folder}${cleanPath}`
  }
  let fullUrl = base + path
  if (token) {
    try {
      const url = new URL(fullUrl)
      url.searchParams.set('token', token)
      fullUrl = url.toString()
    } catch {
      fullUrl += `${fullUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
    }
  }
  if (opts.version) {
    fullUrl += `${fullUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(String(opts.version))}`
  }
  return fullUrl
}

/**
 * Build the upload path for a message attachment.
 * Подставьте в `resolveUploadsUrl` для img / video / audio / ссылки.
 */
export function getAttachmentPath(attachment: {
  file_url?: string | null
  file_path?: string | null
  filename?: string | null
}): string {
  if (!attachment) return ''
  if (attachment.file_url && typeof attachment.file_url === 'string') {
    const u = attachment.file_url.trim()
    if (u && !u.startsWith('telegram_file_id:')) return u
  }
  const fp = attachment.file_path && typeof attachment.file_path === 'string' ? attachment.file_path.trim() : ''
  if (fp && !fp.startsWith('telegram_file_id:')) {
    const p = fp.replace(/^\/+/, '')
    return p.startsWith('uploads/') ? `/${p}` : `/uploads/${p}`
  }
  const fn = attachment.filename && typeof attachment.filename === 'string' ? attachment.filename.trim() : ''
  if (fn) {
    if (fn.startsWith('avatar_')) return `/uploads/avatars/${fn}`
    return `/uploads/attachments/${fn}`
  }
  return ''
}
